var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../button/button';
import '../checkbox-field/checkbox-field';
import '../icon/icons/chevron-down';
import '../icon/icons/chevron-right';
import '../icon/icons/drag-reorder';
import '../skeleton/skeleton';
import styles from './table-cell.styles';
/**
 * @slot default - The table cell content
 * @fires bl-row-cell-select - emitted when slider value is changed
 * @fires bl-draggable-cell-mousedown - emitted when drag icon is clicked
 * @csspart base - The component's base wrapper.
 * @csspart skeleton - The component's skeleton element.
 * @csspart expandable-button - The component's expandable-button wrapper.
 * @csspart icon-chevron-down - The component's chevron-down icon.
 * @csspart draggable-button - The component's draggable-button wrapper.
 * @csspart icon-drag-reorder - The component's drag-reorder icon.
 * @csspart checkbox - The component's checkbox element.
 * @csspart title - The component's title wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart view-button - The component's view-button wrapper.
 * @csspart icon-chevron-right - The component's chevron-right icon.
 */
export class BlocksTableCell extends BlocksElement {
    constructor() {
        super(...arguments);
        /**
         * Loading State
         * - Enables the skeleton loader on cells
         * - _*This is dynamically applied and should not be manually added to the cell_
         */
        this.isLoading = false;
        /**
         * Text to announce when drag button gets focus
         */
        this.startDragText = 'Drag';
        /**
         *
         * To toggle the active state of drag button on touch event
         */
        this.isDragButtonActive = false;
        /**
         * Set the width
         * 1. Add inline width style to cell to adjust the width
         */
        this.setWidth = () => {
            if (this.width) {
                const element = this.shadowRoot?.querySelector('.cat-c-table-cell');
                if (element) {
                    element.style.minWidth = this.width;
                    element.style.width = this.width;
                }
            }
        };
    }
    static { this.styles = styles; }
    /**
     * Gets cat table body
     */
    get _catTableBody() {
        return this.closest('cat-table-body');
    }
    /**
     * First updated lifecycle hook
     * 1. When the component is first updated, run these functions
     * 2. Fire to set the cell colspan scss
     * 3. Fire to set the cell width
     */
    firstUpdated() {
        this.setColspan(); /* 2 */
        this.setWidth(); /* 3 */
    }
    /**
     * Set the colspan
     * 1) Add inline grid-column style to host to adjust the colspan of the item
     */
    setColspan() {
        if (this.colspan && this.colspan > 0) {
            const element = this.shadowRoot?.querySelector('.cat-c-table-cell');
            if (element) {
                element.style.gridColumn = 'auto / span ' + this.colspan; /* 1 */
            }
        }
    }
    /**
     *
     * @fire bl-draggable-cell-mousedown event
     */
    dispatchMouseDownEvent(event) {
        this.dispatch({
            eventName: 'bl-draggable-cell-mousedown',
            detailObj: { event, dragRow: this.parentElement }
        });
    }
    renderLoading(componentClassName) {
        return html `
      <div part="base" class="${componentClassName}">
        <cat-skeleton part="skeleton" exportparts=${this.getExportParts('skeleton')}></cat-skeleton>
      </div>
    `;
    }
    /**
     * If isDraggable is true, drag button is attached.
     * When there is MouseEvent or TouchEvent in the drag button, bl-draggable-cell-mousedown event is dispatched.
     */
    renderDraggable() {
        return html `
      <cat-button
        part="draggable-button"
        exportparts=${this.getExportParts('button-draggable', 'button')}
        class="cat-c-table-cell__draggable-button"
        @mousedown=${(event) => {
            this.dispatchMouseDownEvent(event);
        }}
        @touchstart=${(event) => {
            // preventing default behavior to stop firing of mousedown event
            event.preventDefault();
            this.dispatchMouseDownEvent(event);
            this.isDragButtonActive = true;
        }}
        @touchend=${() => {
            this.isDragButtonActive = false;
        }}
        @keydown=${this.handleKeyDown}
        variant="ghost"
        ?hideText=${true}
        .disabled=${this.disabled}
        @focus=${() => {
            // As during browse mode, the space/enter keydown is not fired. Setting role as application and reverting on blur.
            this._catTableBody?.setAttribute('role', 'application');
        }}
        @blur=${() => {
            const bodyRole = this._catTableBody?.getAttribute('role');
            if (bodyRole === 'application') {
                this._catTableBody?.removeAttribute('role');
            }
        }}
      >
        ${this.startDragText}
        <cat-icon-drag-reorder
          part="icon-drag-reorder"
          exportparts=${this.getExportParts('drag-reorder', 'icon')}
          class="cat-c-table-cell__draggable-icon"
          slot="after"
        ></cat-icon-drag-reorder>
      </cat-button>
    `;
    }
    handleKeyDown(evt) {
        if (evt.key === KEYS.SPACE || evt.key === KEYS.ENTER) {
            evt.preventDefault();
            evt.stopPropagation();
            this.dispatch({ eventName: 'bl-keyboard-drag-start', detailObj: { event: evt, dragRow: this.parentElement } });
            const parentElement = this.closest('cat-table-row')?.shadowRoot?.querySelector('.cat-c-table-row');
            parentElement?.setAttribute('tabindex', '-1');
            parentElement?.focus();
        }
    }
    handleExpandableClick() {
        this.dispatch({
            eventName: 'bl-table-cell-expand',
            detailObj: {}
        });
    }
    render() {
        const componentClassName = this.componentClassNames('cat-c-table-cell', {
            'cat-c-table-cell--align-right': this.align === 'right',
            'cat-c-table-cell--align-center': this.align === 'center',
            'cat-c-table-cell--space-between': this.align === 'space-between',
            'cat-c-table-cell--flex-start': this.align === 'flex-start',
            'cat-c-table-cell--align-top-left': this.align === 'top-left',
            'cat-c-table-cell--align-top-right': this.align === 'top-right',
            'cat-c-table-cell--align-bottom-left': this.align === 'bottom-left',
            'cat-c-table-cell--align-bottom-right': this.align === 'bottom-right',
            'cat-c-table-cell--align-top-center': this.align === 'top-center',
            'cat-c-table-cell--align-bottom-center': this.align === 'bottom-center',
            'cat-c-table-cell--border-right': this.border === 'right',
            'cat-c-table-cell--colspan': this.colspan && this.colspan > 0,
            'cat-c-table-cell--frozen': this.isFrozen === true,
            'cat-c-table-cell--tooltip': this.tooltip === true,
            'cat-is-bulk-action': this.isBulkAction === true,
            'cat-is-expandable': this.isExpandable === true,
            'cat-is-expandable-active': this.isExpandableActive === true,
            'cat-is-expandable-nested-child': this.isExpandableNestedChild === true,
            'cat-is-draggable': this.isDraggable === true,
            'cat-is-active-shadow': this.isActiveShadow === true,
            'cat-is-mobile-view': this.isMobileView === true,
            'cat-is-first': this.isFirst === true,
            'cat-c-table-cell--loader': this.isLoading === true,
            'cat-drag-button-active': this.isDragButtonActive === true
        });
        if (this.isLoading) {
            return this.renderLoading(componentClassName);
        }
        return html `
      <div part="base" role="cell" class="${componentClassName}" aria-disabled=${ifDefined(this.disabled)}>
        ${this.isDraggable === true ? this.renderDraggable() : html `${nothing}`}
        ${this.isExpandable === true
            ? html `
              <cat-button
                part="expandable-button"
                exportparts=${this.getExportParts('button-expandable', 'button')}
                class="cat-c-table-cell__expandable-button"
                variant="ghost"
                ?hideText=${true}
                ?disabled=${this.disabled}
                @click=${this.handleExpandableClick}
                label=${ifDefined(this.arialabel)}
                ?expanded=${this.isExpandableActive ? true : false}
              >
                <cat-icon-chevron-down
                  part="icon-chevron-down"
                  exportparts=${this.getExportParts('chevron-down', 'icon')}
                  class="cat-c-table-cell__expandable-icon"
                  slot="after"
                ></cat-icon-chevron-down>
              </cat-button>
            `
            : html `${nothing}`}
        ${this.isBulkAction === true
            ? html `
              <cat-checkbox-field
                part="checkbox"
                exportparts=${this.getExportParts('checkbox-field')}
                class="cat-c-table-cell__checkbox"
                name="bulk-action-cell-checkbox"
                ?hideLabel=${true}
                arialabel=${ifDefined(this.arialabel)}
                .checked=${this.isBulkActionSelected}
                @bl-checkbox-change=${(e) => {
                e.stopPropagation();
                const path = e.composedPath();
                const isSelected = path[0]?.checked;
                this.dispatch({
                    e,
                    eventName: 'bl-row-cell-select',
                    detailObj: { index: this.bulkActionIndex, isSelected }
                });
            }}
                ?data-bulk-action-index=${this.bulkActionIndex}
                .disabled=${this.disabled}
              >
              </cat-checkbox-field>
            `
            : html `${nothing}`}
        <div part="title" class="cat-c-table-cell__title">${html ` <slot part="part-default"></slot> `}</div>
        ${this.isMobileView === true
            ? html `
              <cat-button
                part="view-button"
                exportparts=${this.getExportParts('button-view', 'button')}
                class="cat-c-table-cell__mobile-view-button"
                variant="ghost"
                ?hideText=${true}
              >
                Open Modal
                <cat-icon-chevron-right
                  part="icon-chevron-right"
                  exportparts=${this.getExportParts('chevron-right', 'icon')}
                  class="cat-c-table-cell__mobile-view-icon"
                  slot="after"
                ></cat-icon-chevron-right>
              </cat-button>
            `
            : html `${nothing}`}
      </div>
    `;
    }
}
__decorate([
    property({ type: Number })
], BlocksTableCell.prototype, "colspan", void 0);
__decorate([
    property({ type: String })
], BlocksTableCell.prototype, "width", void 0);
__decorate([
    property({ type: String })
], BlocksTableCell.prototype, "align", void 0);
__decorate([
    property({ type: String })
], BlocksTableCell.prototype, "border", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTableCell.prototype, "tooltip", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTableCell.prototype, "isBulkAction", void 0);
__decorate([
    property({ type: Number })
], BlocksTableCell.prototype, "bulkActionIndex", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTableCell.prototype, "isBulkActionSelected", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTableCell.prototype, "isExpandable", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTableCell.prototype, "isDraggable", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTableCell.prototype, "isExpandableNestedChild", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTableCell.prototype, "isExpandableActive", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTableCell.prototype, "isActiveShadow", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTableCell.prototype, "isFrozen", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTableCell.prototype, "isFirst", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTableCell.prototype, "isMobileView", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTableCell.prototype, "isLoading", void 0);
__decorate([
    property({ type: Boolean })
], BlocksTableCell.prototype, "disabled", void 0);
__decorate([
    property({ type: String })
], BlocksTableCell.prototype, "arialabel", void 0);
__decorate([
    property({ type: String })
], BlocksTableCell.prototype, "startDragText", void 0);
__decorate([
    state()
], BlocksTableCell.prototype, "isDragButtonActive", void 0);
if (customElements.get('cat-table-cell') === undefined) {
    customElements.define('cat-table-cell', BlocksTableCell);
}
